import { Link } from 'react-router-dom';

import useToast from '@/components/Toast/useToast';
import { FetchError } from '@/data/fetcher';
import ExternalLinkIcon from '@/icons/ExternalLinkIcon.svg?react';
import { appOrganizationPath } from '@/utils/constants';

import { useOrganizationId } from './useSwr';

const ACTION_MAP = {
  feedback: 'Adding feedback to a trace',
  'annotation-queue': 'Adding a trace to an annotation queue',
};

const useToastOn429Error = () => {
  const { createToast } = useToast();
  const tenantId = useOrganizationId();

  const handle429Error = (
    e: Error,
    action: 'feedback' | 'annotation-queue'
  ) => {
    const isLonglivedExceeded = e.message.includes('monthly_longlived_traces');
    const type = isLonglivedExceeded ? 'Extended retention trace' : 'Trace';

    if (e instanceof FetchError && e.status === 429) {
      createToast({
        error: true,
        title: `${type} limit exceeded`,
        description: `${ACTION_MAP[action]} extends its data retention period. This feature is disabled until the end of the month, or the limit is increased.`,
        duration: 10000,
        viewPortClassName: 'w-[500px]',
        children: (
          <div className="flex gap-2 text-ls-gray-300">
            <Link
              className="flex flex-none items-center justify-center gap-1 text-sm underline underline-offset-4"
              to={`/${appOrganizationPath}/${tenantId}/settings/payments?tab=1`}
            >
              Configure limits
            </Link>
            <Link
              className="flex flex-none items-center justify-center gap-1 text-sm"
              to={
                'https://docs.langchain.com/langsmith/administration-overview#usage-limits'
              }
              target="_blank"
            >
              Learn More <ExternalLinkIcon className=" h-4 w-4 " />
            </Link>
          </div>
        ),
      });
    } else {
      throw e;
    }
  };

  const is429Error = (e: Error | undefined) => {
    return e instanceof FetchError && e.status === 429;
  };

  return {
    handle429Error,
    is429Error,
  };
};

export default useToastOn429Error;
